import { useEffect, useRef, useState, type ReactNode } from 'react'
import { IconCheck, IconChevronDown, IconWorld } from '@tabler/icons-react'
import { LANGUAGES } from '@webbuilder/i18n'
import { useI18n } from '../lib/i18n'
import { helpUrl } from '../lib/helpCenter'
import { LEGAL_PATHS } from '../legal/paths'

/**
 * El pie del panel: lo que hay que saber del sitio, fuera de la tarjeta.
 *
 * Una línea pequeña bajo el marco de contenido, en el fondo del panel y no
 * dentro de la tarjeta: es información sobre el producto, no sobre lo que se
 * hace en la pantalla. El derecho de autor, el idioma, la ayuda y lo legal.
 *
 * Los enlaces se abren en otra pestaña: dentro del panel casi siempre hay algo
 * a medias, y una página legal que sustituye a la aplicación cuesta volver por
 * la puerta de entrada.
 *
 * Sin «ajustes de cookies»: el panel no tiene un aviso de consentimiento que
 * configurar —sólo usa las cookies de la sesión—, y un enlace que no abre nada
 * prometería lo que no existe. El aviso de cookies sí está, y dice cuáles son.
 */
export function DashboardFooter({ note }: {
  /** Una nota propia de la pantalla dentro del pie (la beta, en Inicio). */
  note?: ReactNode
}) {
  const { t, language, setLanguage } = useI18n()
  const year = new Date().getFullYear()
  const [menuOpen, setMenuOpen] = useState(false)
  const languageRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  /* Cierra con un clic fuera o con Escape. Escape devuelve el foco al botón: sin
     eso, quien navega con teclado se quedaba sin sitio al cerrar el menú. */
  useEffect(() => {
    if (!menuOpen) return
    const onPointerDown = (event: PointerEvent) => {
      if (!languageRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setMenuOpen(false)
      triggerRef.current?.focus({ preventScroll: true })
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])
  const link = (href: string, label: string) => (
    <a href={href} target="_blank" rel="noreferrer">{label}</a>
  )

  return (
    <footer className="dash-footer">
      <div className={`dash-footer-row${note ? ' dash-footer-row--with-note' : ''}`}>
        <span className="dash-footer-copy">{t('dashfoot.copyright', { year })}</span>
        {note ? <p className="dash-footer-note">{note}</p> : null}

        <div className="dash-footer-actions">
          {/* El idioma: un texto con su globo que abre un menú propio, hacia arriba.
              Antes era un `select` nativo invisible encima: su desplegable se abre
              pegado al borde de abajo, y el navegador, al enfocarlo dentro de un
              marco que no scrollea, lo desplazaba unos píxeles y todo lo de arriba
              quedaba corrido. Un botón visible no necesita que lo metan en vista,
              y el menú, al ser del propio pie, no mueve nada. */}
          <div className="dash-footer-language" ref={languageRef}>
            <button
              ref={triggerRef}
              type="button"
              className="dash-footer-language-trigger"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={t('language.label')}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <IconWorld size={13} aria-hidden="true" />
              <span>{t(`language.${language}`)}</span>
              <IconChevronDown size={11} aria-hidden="true" />
            </button>
            {menuOpen ? (
              <div className="dash-footer-language-menu" role="menu" aria-label={t('language.label')}>
                {LANGUAGES.map((code) => (
                  <button
                    key={code}
                    type="button"
                    role="menuitemradio"
                    aria-checked={code === language}
                    className={code === language ? 'is-current' : ''}
                    onClick={() => { setLanguage(code); setMenuOpen(false) }}
                  >
                    <span>{t(`language.${code}`)}</span>
                    {code === language ? <IconCheck size={13} aria-hidden="true" /> : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <nav className="dash-footer-links" aria-label={t('home.footer.legalAria')}>
            {link(helpUrl(language), t('dashfoot.help'))}
            {link(LEGAL_PATHS.cookies[language], t('dashfoot.cookies'))}
            {link(LEGAL_PATHS.terms[language], t('dashfoot.terms'))}
            {link(LEGAL_PATHS.privacy[language], t('dashfoot.privacy'))}
          </nav>
        </div>
      </div>
    </footer>
  )
}
